import {getUser, navigateTo} from '@genesislcap/foundation-auth';
import {getAuthStore} from '@genesislcap/foundation-auth/store';
import {Auth, Connect} from '@genesislcap/foundation-comms';
import {FoundationRouterConfiguration} from '@genesislcap/foundation-ui';
import {GENESIS_SOCKET_URL, isDev, PUBLIC_PATH} from '@genesislcap/foundation-utils';
import {css, inject, NavigationPhase, Route} from '@genesislcap/web-core';
import {defaultLayout as landingLayout, loginLayout} from '../layouts';
import {defaultLayout as mbmDefaultLayout} from '../../mbm/layouts';
import {defaultLayout as pbiDefaultLayout} from '../../pbi/layouts';
import {AppManagement} from './mbm/app-management/app-management';
import {BondBiddingRoute} from './mbm/bond-bidding/bond-bidding';
import {NotFound} from './mbm/not-found/not-found';
import {defaultNotPermittedRoute, NotPermitted} from './mbm/not-permitted/not-permitted';
import {UserActivityRoute} from './mbm/user-activity/user-activity';
import {ActivityTodayRoute} from './mbm/activity-today/activity-today';
import {SimulatorRoute} from './mbm/simulator/simulator';
import {PlatformActivityRoute} from './mbm/platform-activity';
import {LandingPage} from '../landing';
import bofaLogo from '../../mbm/assets/bofa.svg';
import {LoginSettings} from './types';
import {DealsRoute} from './pbi/deals/deals';
import {Permission} from '../../pbi/constants';
import {AppMode, ModeService} from '../services/mode-service';
import {getAuthMachine} from '@genesislcap/foundation-auth/machine';
import {profiles, users} from '../../pbc/auth/routes';
import {CashProData} from './pbi/cash-pro-data/cash-pro-data';
import { ensureLatestAppVersion } from '../main/app-version-check';

function clearLoginFormAfterLogout(): void {
  try {
    const { formEntry } = getAuthStore();
    const mutableFormEntry = formEntry as typeof formEntry & { autofilling: boolean };

    const clearFields = () => {
      formEntry.onUsernameChanged(new CustomEvent('auth-username-changed', { detail: '' }));
      formEntry.onPasswordChanged(new CustomEvent('auth-password-changed', { detail: '' }));
      formEntry.onShowPasswordChanged(new CustomEvent('auth-showPassword-changed', { detail: false }));
    };

    mutableFormEntry.autofilling = true;
    clearFields();
    queueMicrotask(() => {
      clearFields();
      mutableFormEntry.autofilling = false;
    });
  } catch {
    // Auth MFE store not registered on this path.
  }
}

type FoundationRouteNavItem = ReturnType<
  FoundationRouterConfiguration<LoginSettings>['getNavItems']
>[number];
type ModeAwareNavItem = FoundationRouteNavItem & {
  modes?: AppMode[];
  adminOnly?: boolean;
};

// eslint-disable-next-line
declare var ENABLE_SSO: string;

// Enable SSO either via build flag or by matching known production/UAT hostnames
const ssoSettings = (() => {
  const hostnamesEnablingSso = [
    'munis.bofa.com',
    'munis.bankofamerica.com',
    'munis.uat.bofa.com',
    'munis.uat.bankofamerica.com',
  ];

  const hostname =
    typeof window !== 'undefined' && window.location ? window.location.hostname : '';

  const enabledByEnv = typeof ENABLE_SSO !== 'undefined' && ENABLE_SSO === 'true';
  const enabledByHost = hostnamesEnablingSso.includes(hostname);
  const enabled = enabledByEnv || enabledByHost;

  return enabled
    ? {
        autoAuth: true,
        sso: {
          toggled: true,
          identityProvidersPath: 'sso/list',
        },
      }
    : {};
})();

const publicPath = typeof PUBLIC_PATH !== 'undefined' ? PUBLIC_PATH : '';

export const ROUTE_LOGIN = 'login';
export const ROUTE_LANDING = 'landing';
export const ROUTE_HOME = 'home';
export const ROUTE_BOND_BIDDING = 'bond-bidding';
export const ROUTE_USERS = 'users';
export const ROUTE_ACTIVITY_TODAY = 'activity-today';
export const ROUTE_PLATFORM_ACTIVITY = 'platform-activity';
export const ROUTE_WATCHLIST = 'watchlist';
export const ROUTE_SIMULATOR = 'simulator';
export const ROUTE_USER_ACTIVITY = 'user-activity';

export const DEFAULT_ROUTE_BY_MODE: Record<AppMode, string> = {
  [AppMode.Landing]: ROUTE_LANDING,
  [AppMode.PBI]: ROUTE_HOME,
  [AppMode.MBM]: ROUTE_BOND_BIDDING,
  [AppMode.UserManagement]: ROUTE_USERS,
};

/**
 * Listens for the `auth-change-password` event and redirects to the login page
 * after a successful password change. Waits for the auth machine to leave the
 * pending state before checking the outcome. Uses exponential backoff
 * (1s, 2s, 4s, 8s, 16s) if the server response has not yet arrived.
 */
function setupPasswordChangeRedirect() {
  document.addEventListener('auth-change-password', () => {
    const check = (retries = 0, delay = 1000) => {
      try {
        const machine = getAuthMachine();
        if (machine.hasTag('pending')) {
          if (retries < 5) setTimeout(() => check(retries + 1, delay * 2), delay);
          return;
        }
        if (!machine.hasTag('error')) {
          navigateTo(publicPath + '/login');
        }
      } catch {}
    };
    setTimeout(() => check(), 2000);
  });
}


/**
 * Determines if the simulator should be shown based on environment
 * Shows simulator in development mode or when running on QA environment
 */
const shouldShowSimulator = (): boolean => {
  // Show in development mode
  if (isDev()) {
    return true;
  }

  const hostname = window.location.hostname;
  // Show on QA environments (detect by hostname containing or specific QA domains)
  const validHostnames = [
    'mbm-ioi-mbm-bofa-qa.cddev.genesis.global',
    'mbm-ioi-multi-cluster.cddev.genesis.global',
    'qa-bofa-mbmioi1.cddev.genesis.global',
    'munis-portal-multi-cluster-qa.cddev.genesis.global',
  ];
  if (validHostnames.includes(hostname)) {
    return true;
  }

  return false;
};

export class MainCombinedRouterConfig extends FoundationRouterConfiguration<LoginSettings> {
  @Connect private connect!: Connect;
  @Auth private auth!: Auth;
  @inject(ModeService) private modeService!: ModeService;

  async configure() {
    this.configureAnalytics();
    this.configureRoutePermittedChecks();
    setupPasswordChangeRedirect();
    this.routes.fallback(() => {
      if (!this.auth.isLoggedIn) {
        return { redirect: ROUTE_LOGIN };
      }

      const mode = this.modeService.getMode();
      const path = mode ? DEFAULT_ROUTE_BY_MODE[mode] : ROUTE_LANDING;
      return { redirect: path };
    });

    this.title = 'Munis Portal';
    this.defaultLayout = landingLayout;

    const authPath = ROUTE_LOGIN;

    this.routes.map(
      { path: '', redirect: authPath },
      {
        path: authPath,
        name: 'login',
        title: 'Login',
        element: async () => {
          const { configure, defaultAuthConfig } = await import(
            '@genesislcap/foundation-auth/config'
          );
          return configure({
            omitRoutes: ['request-account', 'forgot-password'],
            fields: {
              ...defaultAuthConfig.fields,
              username: {
                ...defaultAuthConfig.fields.username,
                pattern: '^[a-zA-Z0-9.@_\\-:]*$',
              },
            },
            hostPath: this.loginPath,
            postLoginRedirect: async () => {
              await ensureLatestAppVersion();
              await this.connect.connect(GENESIS_SOCKET_URL);
              const lastPath =
                getUser().lastPath() === `${publicPath}/` ? undefined : getUser().lastPath();
              const mode = this.modeService.getMode();
              const defaultPath =
                publicPath + '/' + (mode ? DEFAULT_ROUTE_BY_MODE[mode] : ROUTE_LANDING);
              navigateTo(lastPath ?? defaultPath);
            },
            postLogoutRedirect: async () => {
              clearLoginFormAfterLogout();
              if (this.connect.isConnected) {
                this.connect.disconnect();
              }
              await ensureLatestAppVersion();
              document.dispatchEvent(new CustomEvent('logout'));
              // Small delay to allow proper cleanup before default auth redirect
              setTimeout(() => {
                defaultAuthConfig.postLogoutRedirect();
              }, 100);
            },
            logo: css`
              content: url(${bofaLogo});
              width: 80px;
              height: auto;
            `,
            logoAltText: 'Bank of America',
            ...ssoSettings,
          });
        },
        layout: loginLayout,
        settings: { public: true, modes: [AppMode.Landing, AppMode.PBI, AppMode.MBM, AppMode.UserManagement] },
        childRouters: true,
      },
      { path: 'not-found', element: NotFound, title: 'Not Found', name: 'not-found' },
      {
        path: defaultNotPermittedRoute,
        element: NotPermitted,
        title: 'Not Permitted',
        name: defaultNotPermittedRoute,
      },
      {
        path: ROUTE_LANDING,
        element: LandingPage,
        title: 'Home',
        name: ROUTE_LANDING,
        layout: landingLayout,
        navItems: [
          {
            title: 'Home',
            icon: {
              name: 'bank',
              variant: 'solid',
            },
          },
        ],
        settings: { modes: [AppMode.Landing, AppMode.PBI, AppMode.MBM, AppMode.UserManagement], defaultMode: AppMode.Landing },
      },
      {
        path: ROUTE_BOND_BIDDING,
        element: BondBiddingRoute,
        title: 'Live Activity',
        name: ROUTE_BOND_BIDDING,
        layout: mbmDefaultLayout,
        navItems: [
          {
            title: 'Live Activity',
            icon: {
              name: 'bank',
              variant: 'solid',
            },
            permission: 'ViewLiveActivity',
            modes: [AppMode.MBM],
          } as any,
        ],
        settings: { modes: [AppMode.MBM] },
      },
      {
        path: ROUTE_ACTIVITY_TODAY,
        element: ActivityTodayRoute,
        title: 'Activity Today',
        name: ROUTE_ACTIVITY_TODAY,
        layout: mbmDefaultLayout,
        navItems: [
          {
            title: "Today's Activity",
            icon: {
              name: 'bank',
              variant: 'solid',
            },
            permission: 'ViewTodaysActivity',
            modes: [AppMode.MBM],
          } as any,
        ],
        settings: { modes: [AppMode.MBM] },
      },
      {
        path: ROUTE_PLATFORM_ACTIVITY,
        element: PlatformActivityRoute,
        title: 'Platform Activity',
        name: ROUTE_PLATFORM_ACTIVITY,
        layout: mbmDefaultLayout,
        navItems: [
          {
            title: 'Platform Activity',
            icon: {
              name: 'bank',
              variant: 'solid',
            },
            permission: 'ViewPlatformActivity',
            modes: [AppMode.MBM],
          } as any,
        ],
        settings: { modes: [AppMode.MBM] },
      },
      {
        path: ROUTE_WATCHLIST,
        element: AppManagement,
        title: 'Watchlist',
        name: ROUTE_WATCHLIST,
        layout: mbmDefaultLayout,
        navItems: [
          {
            title: 'Watchlist',
            icon: {
              name: 'cog',
              variant: 'solid',
            },
            permission: 'ViewWatchlist',
            modes: [AppMode.MBM],
          } as any,
        ],
        settings: { modes: [AppMode.MBM] },
      },
      {
        path: ROUTE_SIMULATOR,
        element: SimulatorRoute,
        title: 'Simulator',
        name: ROUTE_SIMULATOR,
        layout: mbmDefaultLayout,
        navItems: shouldShowSimulator()
          ? ([
              {
                title: 'Simulator',
                icon: {
                  name: 'play',
                  variant: 'solid',
                },
                permission: 'SimulatorAccess',
                modes: [AppMode.MBM],
              } as any,
            ] as any)
          : [],
        settings: { modes: [AppMode.MBM] },
      },
      {
        path: ROUTE_USER_ACTIVITY,
        element: UserActivityRoute,
        title: 'User Activity',
        name: ROUTE_USER_ACTIVITY,
        layout: mbmDefaultLayout,
        navItems: [
          {
            title: 'User Activity',
            icon: {
              name: 'user',
              variant: 'solid',
            },
            permission: 'ViewUserActivity',
            modes: [AppMode.MBM],
          } as any,
        ],
        settings: { modes: [AppMode.MBM] },
      },
      {
        path: ROUTE_HOME,
        element: DealsRoute,
        title: 'Deal Listing',
        name: 'deals',
        layout: pbiDefaultLayout,
        navItems: [
          {
            title: 'Deals',
            icon: {
              name: 'bank',
              variant: 'solid',
            },
            permission: Permission.VIEW_DEAL_DETAILS,
            modes: [AppMode.PBI],
          } as any,
        ],
        settings: { modes: [AppMode.PBI] },
      },
      {
        ...users,
        layout: pbiDefaultLayout,
        navItems: (users.navItems || []).map((item) => ({
          ...item,
          modes: [AppMode.UserManagement],
        })) as any,
        settings: {
          ...(users.settings || {}),
          modes: [AppMode.UserManagement],
        },
      },
      {
        ...profiles,
        layout: pbiDefaultLayout,
        navItems: (profiles.navItems || []).map((item) => ({
          ...item,
          modes: [AppMode.UserManagement],
        })) as any,
        settings: {
          ...(profiles.settings || {}),
          modes: [AppMode.UserManagement],
        },
      },
      {
        path: 'cash-pro-data',
        element: CashProData,
        title: 'Cash Pro Data',
        name: 'cash-pro-data',
        navItems: [
          {
            title: 'Cash Pro Data',
            icon: {
              name: 'database',
              variant: 'solid',
            },
            adminOnly: true,
          } as any,
        ],
        settings: {
          isPermitted: () => this.auth.currentUser?.hasPermission?.('DatabaseDataView') ?? false,
        } as LoginSettings,
      },
    );

    this.contributors.push({
      navigate: async (phase: NavigationPhase) => {
        const settings = phase.route.settings as any;

        // If the route is public or the user is authenticated don't block
        if (settings?.public || this.user.isAuthenticated) {
          if (settings?.defaultMode) {
            this.modeService.setMode(settings.defaultMode);
          }

          const allowedModes: AppMode[] | undefined = settings?.modes;
          const mode = this.modeService.getMode();

          if (allowedModes && mode && !allowedModes.includes(mode)) {
            const redirectRouteName = this.defaultPathForMode(mode);
            phase.cancel(() => {
              Route.name.replace(phase.router, redirectRouteName);
            });
          }

          return;
        }

        // Otherwise route them to login
        this.navigationPhaseLoginRedirect(phase);
      },
    });
  }

  private defaultPathForMode(mode: AppMode | undefined): string {
    return mode ? DEFAULT_ROUTE_BY_MODE[mode] : ROUTE_LANDING;
  }

  // Filter navigation items so only items for the active mode are shown
  getNavItems(): FoundationRouteNavItem[] {
    // Call base implementation and then apply mode-based filtering
    const allNavItems = (super.getNavItems ? super.getNavItems() : []) as ModeAwareNavItem[];
    const mode = this.modeService.getMode();
    const isAdmin = this.auth.currentUser?.hasPermission?.('DatabaseDataView') ?? false;

    if (!mode) {
      return allNavItems as FoundationRouteNavItem[];
    }

    // Nav items carry modes / admin metadata directly; use it for filtering
    return allNavItems
      .filter((item) => {
        if (item.adminOnly && !isAdmin) {
          return false;
        }
        const allowedModes = item.modes;
        return !allowedModes || allowedModes.includes(mode);
      })
      .map((item) => item as FoundationRouteNavItem);
  }
}

